import React, { useState, useEffect } from "react";

import {
  DataGridPro,
  gridClasses,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
  useGridApiRef,
} from "@mui/x-data-grid-pro"; // Changed to Pro package
import Box from "@mui/material/Box";
import {
  Tabs,
  Tab,
  styled,
  alpha,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Slider,
} from "@mui/material";
import { hitter_columns, hitter_columns_mobile, pitcher_columns, pitcher_columns_mobile } from "./AflColumnDefs";
import { useMobileZoom } from "../utils/useMobileZoom";
import { useHeaderFilters } from "../utils/useHeaderFilters";

const ODD_OPACITY = 0.2;

const StripedDataGrid = styled(DataGridPro)(({ theme }) => ({
  [`& .${gridClasses.row}.even`]: {
    backgroundColor: theme.palette.grey[200],
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      "&:hover": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
          theme.palette.action.selectedOpacity +
          theme.palette.action.hoverOpacity
        ),
        // Reset on touch devices, it doesn't add specificity
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
  [`& .${gridClasses.row}.odd`]: {
    backgroundColor: "white",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, ODD_OPACITY),
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
    "&.Mui-selected": {
      backgroundColor: alpha(
        theme.palette.primary.main,
        ODD_OPACITY + theme.palette.action.selectedOpacity
      ),
      "&:hover": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          ODD_OPACITY +
          theme.palette.action.selectedOpacity +
          theme.palette.action.hoverOpacity
        ),
        // Reset on touch devices, it doesn't add specificity
        "@media (hover: none)": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            ODD_OPACITY + theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
}))

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  isDesktop?: boolean;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, isDesktop, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3, ...(isDesktop ? {} : { px: 0 }) }}>{children}</Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}




export default function Leaderboard(props: any) {
  const apiRef = useGridApiRef(); // Added for Pro API access

  const [value, setValue] = React.useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const [hitterData, setHitterData] = useState([]);
  const [pitcherData, setPitcherData] = useState([]);

  const [seasons, setSeasons] = useState<string[]>([]);
  const [season, setSeason] = useState<string>("");

  const handleSeason = (event: SelectChangeEvent) => {
    setSeason(event.target.value as string);
  };

  // The backend derives the season list from the files on disk, so a new AFL
  // year appears here without a frontend change.
  useEffect(() => {
    fetch(props.prod ? "https://oriolebird.pythonanywhere.com/afl/seasons" : "/afl/seasons")
      .then((res) => res.json())
      .then((data) => {
        setSeasons(data.seasons ?? []);
        setSeason((current) => current || data.default || "");
      })
      .catch(() => { });
  }, [props.prod]);

  const h_columns = props.isDesktop
    ? hitter_columns
    : hitter_columns_mobile;
  const p_columns = props.isDesktop
    ? pitcher_columns
    : pitcher_columns_mobile;

  // const h_columns = hitter_columns
  // const p_columns = pitcher_columns


  //console.log(season);
  const [qualifier, setQualifier] = React.useState("1");

  const handleQualifier = (event: SelectChangeEvent) => {
    setQualifier(event.target.value as string);
  };

  const [organization, setOrganization] = React.useState("All");

  const handleOrganization = (event: SelectChangeEvent) => {
    setOrganization(event.target.value as string);
  };

  const [aflTeam, setAflTeam] = React.useState("All");

  const handleAflTeam = (event: SelectChangeEvent) => {
    setAflTeam(event.target.value as string);
  };


  const [ageRange, setAgeRange] = React.useState<number[]>([16, 28]);

  const handleAgeRange = (event: Event, newValue: any) => {
    setAgeRange(newValue);
  };

  const { gridSx, wrapperSx, zoomButtons } = useMobileZoom(props.isDesktop);
  const { headerFilters, headerFilterButton } = useHeaderFilters();

  // Bat tracking is venue-gated and absent league-wide in some seasons. Held in
  // state (not derived) so the Columns toolbar toggles still stick.
  const [hitterVisibility, setHitterVisibility] = useState<Record<string, boolean>>({});

  useEffect(() => {
    setHitterVisibility((current) => ({
      ...current,
      bat_speed: hitterData.some((row: any) => typeof row.bat_speed === "number"),
    }));
  }, [hitterData]);

  function CustomToolbar() {
    return (
      <GridToolbarContainer sx={{ backgroundColor: "#FFFFFF" }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        {headerFilterButton}
        <GridToolbarDensitySelector />
        <GridToolbarExport
        />
        {zoomButtons}
        {/* Added Quick Filter */}
        <GridToolbarQuickFilter
          quickFilterParser={(searchInput) =>
            searchInput.split(',').map((value) => value.trim())
          }
          sx={{ marginLeft: 'auto', width: props.isDesktop ? 500 : 250 }}
        />
      </GridToolbarContainer>
    );
  }


  useEffect(() => {
    if (!season) return;
    const base = props.prod ? "https://oriolebird.pythonanywhere.com" : "";
    const params = qualifier + "/" + ageRange[0] + "/" + ageRange[1] + "/" + season;

    fetch(base + "/afl/hitters/" + params)
      .then((res) => res.json())
      .then((data) => {
        setHitterData(data.data);
      });
    fetch(base + "/afl/pitchers/" + params)
      .then((res) => res.json())
      .then((data) => {
        setPitcherData(data.data);
      });
  }, [props.prod, qualifier, ageRange, season]);

  return (
    <div style={{ width: "100%" }}>
      <title>Arizona Fall League - Prospect Savant</title>
      <meta
        name="description"
        content="Prospect Savant - Minor League Baseball Savant"
      />
      <meta
        name="title"
        content="Prospect Savant"
      />
      <meta property="og:description" content="Prospect Savant - Minor League Baseball Savant" />
      <meta property="og:title" content="Prospect Savant" />
      <Box sx={{ width: "100%", marginTop: "-15px" }}>
        <Box
          sx={{ borderBottom: 1, borderColor: "divider" }}
          paddingLeft={"50px"}
          flexDirection="row"
        >
          <Tabs value={value} onChange={handleChange}>
            <Tab label="Hitters" {...a11yProps(0)} />
            <Tab label="Pitchers" {...a11yProps(1)} />
          </Tabs>
        </Box>
        <div
          style={{
            marginTop: "10px",
            marginBottom: "-20px",
            marginLeft: "30px",
            verticalAlign: "baseline",
          }}
        >
          <FormControl>
            <InputLabel id="afl-season-select-label">Season</InputLabel>
            <Select
              labelId="afl-season-select-label"
              value={seasons.includes(season) ? season : ""}
              label="Season"
              onChange={handleSeason}
              sx={{ width: "100px" }}
            >
              {seasons.map((s) => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl style={{ marginLeft: "10px" }}>
            <InputLabel id="demo-simple-select-label">PA Qualifier</InputLabel>
            <Select
              value={qualifier}
              label="PA Qualifier"
              onChange={handleQualifier}
              defaultValue="1"
              sx={{ width: "102px" }}
            >
              <MenuItem value={1}>1</MenuItem>
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
          <FormControl style={{ marginLeft: "10px" }}>
            <InputLabel id="demo-simple-select-label">Organization</InputLabel>
            <Select
              value={organization}
              label="Organization"
              onChange={handleOrganization}
              defaultValue="All"
              sx={{ width: "100px", }}
            >
              <MenuItem value={"All"}>All</MenuItem>
              <MenuItem value={"ARI"}>ARI</MenuItem>
              <MenuItem value={"ATL"}>ATL</MenuItem>
              <MenuItem value={"ATH"}>ATH</MenuItem>
              <MenuItem value={"BAL"}>BAL</MenuItem>
              <MenuItem value={"BOS"}>BOS</MenuItem>
              <MenuItem value={"CHC"}>CHC</MenuItem>
              <MenuItem value={"CHW"}>CHW</MenuItem>
              <MenuItem value={"CIN"}>CIN</MenuItem>
              <MenuItem value={"CLE"}>CLE</MenuItem>
              <MenuItem value={"COL"}>COL</MenuItem>
              <MenuItem value={"DET"}>DET</MenuItem>
              <MenuItem value={"HOU"}>HOU</MenuItem>
              <MenuItem value={"KCR"}>KCR</MenuItem>
              <MenuItem value={"LAA"}>LAA</MenuItem>
              <MenuItem value={"LAD"}>LAD</MenuItem>
              <MenuItem value={"MIA"}>MIA</MenuItem>
              <MenuItem value={"MIL"}>MIL</MenuItem>
              <MenuItem value={"MIN"}>MIN</MenuItem>
              <MenuItem value={"NYM"}>NYM</MenuItem>
              <MenuItem value={"NYY"}>NYY</MenuItem>
              <MenuItem value={"PHI"}>PHI</MenuItem>
              <MenuItem value={"PIT"}>PIT</MenuItem>
              <MenuItem value={"SDP"}>SDP</MenuItem>
              <MenuItem value={"SEA"}>SEA</MenuItem>
              <MenuItem value={"SFG"}>SFG</MenuItem>
              <MenuItem value={"STL"}>STL</MenuItem>
              <MenuItem value={"TBR"}>TBR</MenuItem>
              <MenuItem value={"TEX"}>TEX</MenuItem>
              <MenuItem value={"TOR"}>TOR</MenuItem>
              <MenuItem value={"WSN"}>WSN</MenuItem>
            </Select>
          </FormControl>
          <FormControl style={{ marginLeft: "10px" }}>
            <InputLabel id="demo-simple-select-label">AFL Team</InputLabel>
            <Select
              value={aflTeam}
              label="AFL Team"
              onChange={handleAflTeam}
              defaultValue="All"
              sx={{ width: "100px", }}
            >
              <MenuItem value={"All"}>All</MenuItem>
              <MenuItem value={"GDD"}>Glendale Desert Dogs</MenuItem>
              <MenuItem value={"MSS"}>Mesa Solar Sox</MenuItem>
              <MenuItem value={"PEJ"}>Peoria Javelinas</MenuItem>
              <MenuItem value={"SRR"}>Salt River Rafters</MenuItem>
              <MenuItem value={"SCO"}>Scottsdale Scorpions</MenuItem>
              <MenuItem value={"SUR"}>Surprise Saguaros</MenuItem>
            </Select>
          </FormControl>
          {!props.isDesktop && <br></br>}
          <span style={{ position: "absolute", marginLeft: props.isDesktop ? "25px" : "0px", marginTop: "3px", color: "#5A6465", fontSize: 16 }}>Age Range</span>
          <Slider
            getAriaLabel={() => 'Age Range'}
            value={ageRange}
            onChange={handleAgeRange}
            valueLabelDisplay="on"
            min={16}
            max={40}
            disableSwap={true}
            sx={{
              width: "200px", marginLeft: props.isDesktop ? "30px" : "10px", marginTop: "25px",
              '& .MuiSlider-valueLabel': {
                fontSize: 11,
                fontWeight: 'bold',
                marginTop: "33px",
                backgroundColor: 'unset',
                color: '#FFFFFF',
                '&::before': {
                  display: 'none',
                },
                '& *': {
                  background: 'transparent',
                  color: '#FFFFFF',
                },
              },
            }}
          />
          
        </div>
        <CustomTabPanel value={value} index={0} isDesktop={props.isDesktop}>
          <Box sx={wrapperSx}>
          <StripedDataGrid
            apiRef={apiRef} // Added for Pro API access
            rows={organization === "All" && aflTeam === "All"
              ? hitterData
              : organization === "All"
                ? hitterData.filter((row: any) => row.afl_team === aflTeam)
                : aflTeam === "All"
                  ? hitterData.filter((row: any) => row.MLB_AbbName === organization)
                  : hitterData.filter((row: any) => row.MLB_AbbName === organization && row.afl_team === aflTeam)}
            sx={{
              height: "80vh",
              width: "100%",
              ...gridSx,
            }}
            columns={h_columns}
            columnVisibilityModel={hitterVisibility}
            onColumnVisibilityModelChange={setHitterVisibility}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            pageSizeOptions={[25, 50, 100]}

            disableColumnResize={false} // Enable column resizing
            headerFilters={headerFilters}

            slots={{
              toolbar: CustomToolbar,
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: "ops", sort: "desc" }],
              },
              density: "compact",
              pinnedColumns: {
                left: ['batter_name'], // Pin player name by default
              }, // Initial pinned columns
            }}
          />
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1} isDesktop={props.isDesktop}>
          <Box sx={wrapperSx}>
          <StripedDataGrid
            apiRef={apiRef} // Added for Pro API access
            sx={{
              height: "80vh",
              width: "100%",
              ...gridSx,
            }}
            rows={organization === "All" && aflTeam === "All"
              ? pitcherData
              : organization === "All"
                ? pitcherData.filter((row: any) => row.afl_team === aflTeam)
                : aflTeam === "All"
                  ? pitcherData.filter((row: any) => row.MLB_AbbName === organization)
                  : pitcherData.filter((row: any) => row.MLB_AbbName === organization && row.afl_team === aflTeam)}
            columns={p_columns}
            getRowClassName={(params) =>
              params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd"
            }
            pagination
            pageSizeOptions={[25, 50, 100]}

            disableColumnResize={false} // Enable column resizing
            headerFilters={headerFilters}

            slots={{
              toolbar: CustomToolbar,
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: "weighted_psStuff", sort: "desc" }],
              },
              density: "compact",
              pinnedColumns: {
                left: ['pitcher_name'], // Pin player name by default
              }, // Initial pinned columns
            }}
          />
          </Box>
        </CustomTabPanel>
      </Box>
    </div>
  );
}
